import { Box } from '@mui/material';
import {
  ETranscoderProfileKind,
  ETranscoderProfileType,
  TranscoderProfile,
  TranscoderVideoProfile,
  useDeleteTranscoderVideoProfileMutation,
} from '@vxui/common';
import { CheckBox, Table } from '@vxui/components';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandProfile } from '../components/ExpandProfile';
import { SubheaderContainer } from '../components/SubheaderContainer';
import { useActionModal } from '../modals/useActionModal';
import { filter } from '../profilesTable.helpers';
import { skipNewRowSorting } from '../profilesTable.sorting';
import { tableStyle } from '../profilesTable.styles';
import { VideoTableProps } from '../profilesTable.types';
import { useTemplateFilter } from '../templateFilter.helpers';
import { getColumns } from './videoTable.helpers';

const rowDisabledCriteria = (row: TranscoderVideoProfile) => row.type === ETranscoderProfileType.new;

export const VideoTable = forwardRef(
  (
    {
      data,
      actions,
      isLoading,
      editableRows,
      profileToDelete,
      onColumnChange,
      onResolutionChange,
      onCodecChange,
      onSelectChange,
      onCloseDeleteModalHandler,
    }: VideoTableProps,
    ref?,
  ) => {
    const { t } = useTranslation();
    const columns = getColumns({
      t,
      actions,
      onChangeHandler: onColumnChange,
      onResolutionChangeHandler: onResolutionChange,
      onCodecChangeHandler: onCodecChange,
      onSelectChangeHandler: onSelectChange,
      editableRows,
    });
    const [templateFilterState, templates] = useTemplateFilter(data);
    const [templateName] = templateFilterState;

    const [filterText, setFilterText] = useState('');
    const [deleteVideoProfile] = useDeleteTranscoderVideoProfileMutation();
    const rows: TranscoderVideoProfile[] = data ? data.filter(filter(filterText.toLowerCase(), templateName)) : [];
    const selectProps = { indeterminate: (isIndeterminate: boolean) => isIndeterminate };

    const { selectedProfiles, toggledClearRows, onSelectedRowsChangeHandler, subHeaderActions, components } =
      useActionModal({
        kind: ETranscoderProfileKind.video,
        profileToDelete,
        onCloseDeleteModalHandler,
        deleteAction: deleteVideoProfile,
      });

    return (
      <Box ref={ref} sx={tableStyle}>
        {components.singleDelete}
        {components.bulkDelete}
        <Table
          id="transcoder-video-profile"
          className="profiles-table"
          fixedHeader
          fixedHeaderScrollHeight="calc(100vh - 320px)"
          columns={columns}
          data={rows}
          dense
          progressPending={isLoading}
          selectableRows
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          selectableRowsComponent={CheckBox}
          selectableRowsComponentProps={selectProps}
          selectableRowDisabled={rowDisabledCriteria}
          onSelectedRowsChange={onSelectedRowsChangeHandler}
          expandableRows={true}
          expandableRowsComponent={props => <ExpandProfile profile={props.data} />}
          expandableRowDisabled={(row: TranscoderProfile) => !row.templates || row.templates?.length === 0}
          subHeader
          sortFunction={skipNewRowSorting}
          subHeaderComponent={
            <SubheaderContainer
              filterText={filterText}
              setFilterText={setFilterText}
              subHeaderActions={subHeaderActions}
              templateFilterState={templateFilterState}
              templates={templates}
              selectedProfiles={selectedProfiles}
            />
          }
          clearSelectedRows={toggledClearRows}
        />
      </Box>
    );
  },
);
